import Config from "config"
import { Auth } from "common/logic/Auth"
import axios, { AxiosResponse } from 'axios'

type ErrorHandler = ((error: unknown) => void) | null

let _errorHandler: ErrorHandler = null

class API {

    static onError(handler: ErrorHandler): void {
        _errorHandler = handler
    }

    static request(params: Record<string, unknown>, authenticated = true, silent = false): Promise<AxiosResponse<unknown>> {
        if (authenticated) {
            const credentials = Auth.getCredentials()
            params.auth = {
                username: credentials.username,
                password: credentials.password
            }
        }
        if (!params.url || (!(params.url as string).startsWith("https://") || !(params.url as string).startsWith("http://"))) {
            const url = params.url as string
            const urlSeparator = url.startsWith("/") ? "" : "/"
            params.url = Config.apiHost + urlSeparator + url
        }
        return axios(params as any).catch(function (error: unknown) {
            if (_errorHandler && (error as any).response && !silent) {
                _errorHandler(error)
            }
            throw error
        })
    }

    static post(url: string, data?: unknown, urlParams: Record<string, unknown> | null = null): Promise<AxiosResponse<unknown>> {
        const params: Record<string, unknown> = { method: 'post', url, data }
        if (urlParams) {
            params.params = urlParams
        }
        return this.request(params)
    }

    static postFile(url: string, file: File, urlParams: Record<string, unknown> | null = null): Promise<AxiosResponse<unknown>> {
        const data = new FormData()
        data.append("myFile", file, file.name)
        const params: Record<string, unknown> = { method: 'post', url, data }
        if (urlParams) {
            params.params = urlParams
        }
        params.headers = { 'content-type': 'multipart/form-data' }
        return this.request(params)
    }

    static put(url: string, data?: unknown, urlParams: Record<string, unknown> | null = null): Promise<AxiosResponse<unknown>> {
        const params: Record<string, unknown> = { method: 'put', url, data }
        if (urlParams) {
            params.params = urlParams
        }
        return this.request(params)
    }

    static get(url: string, urlParams: Record<string, unknown> | null = null): Promise<AxiosResponse<unknown>> {
        const params: Record<string, unknown> = { method: 'get', url }
        if (urlParams) {
            params.params = urlParams
        }
        return this.request(params)
    }

    static delete(url: string, data?: unknown, urlParams: Record<string, unknown> | null = null): Promise<AxiosResponse<unknown>> {
        const params: Record<string, unknown> = { method: 'delete', url }
        if (data !== undefined) {
            params.data = data
        }
        if (urlParams) {
            params.params = urlParams
        }
        return this.request(params)
    }

    static multiple(mapping: Record<string, Promise<AxiosResponse<unknown>>>): Promise<Record<string, unknown>> {
        return Promise.all(
            Object.entries(mapping).map((e) =>
                e[1].then(response => [e[0], (response as AxiosResponse<unknown>).data])
            )
        ).then(response => {
            const accumulator: Record<string, unknown> = {}
            response.forEach((r) => {
                accumulator[r[0] as string] = r[1]
            })
            return accumulator
        })
    }

    static getVariable(name: string, type: string, silent = true): Promise<AxiosResponse<unknown>> {
        return this.request({ method: 'get', url: `/variables/${type}/${name}` }, true, silent)
    }

    static setVariable(name: string, value: unknown, type?: string): Promise<AxiosResponse<unknown>> {
        if (!type) {
            if (value === parseInt(String(value), 10)) {
                type = "int"
            } else {
                type = "string"
            }
        }
        return API.put(`/variables/${type}/${name}`, { value })
    }
}

export default API
